import { useMemo, useState } from 'react';
import { Loader2, AlertTriangle } from 'lucide-react';
import { useLockers, useActiveLockerAssignments } from '@/hooks';
import LockerGrid from '@/components/lockers/LockerGrid';
import { RoomChip } from '@/components/lockers/RoomChip';
import { formatRoom, lockersInRoom, openAssignmentsByLockerId } from '@/lib/locker-utils';
import type { LockerRoom } from '@/types';

interface CheckInLockerPickerProps {
  memberId: string;
  /** Locker room derived from the member's gender by the host modal. */
  room: LockerRoom;
  selectedLockerId: string | null;
  /** Clicking the selected tile again passes null (deselect). */
  onSelect: (lockerId: string | null) => void;
}

/**
 * Optional locker handout inside the check-in modal. Selection is pure local
 * state — the assignment is written (and logged) by the check-in submit
 * sequence, never here. Mounted only when the member's gender names a room,
 * which is also what gates the locker queries.
 */
export default function CheckInLockerPicker({ memberId, room, selectedLockerId, onSelect }: CheckInLockerPickerProps) {
  const { data: lockers, isLoading: lockersLoading, isError: lockersError } = useLockers();
  const { data: activeAssignments, isLoading: assignmentsLoading, isError: assignmentsError } = useActiveLockerAssignments();

  // One snapshot is enough for the occupied-tile tooltips — the modal is
  // short-lived and occupied tiles are inert here (no minute ticker).
  const [now] = useState(() => Date.now());

  const roomLockers = useMemo(() => lockersInRoom(lockers, room), [lockers, room]);

  const openByLockerId = useMemo(
    () => openAssignmentsByLockerId(activeAssignments),
    [activeAssignments],
  );

  const alreadyHeld = useMemo(
    () => (activeAssignments ?? []).filter((assignment) => assignment.member_id === memberId),
    [activeAssignments, memberId],
  );

  const freeCount = roomLockers.filter((locker) => !openByLockerId.has(locker.id)).length;

  const isLoading = lockersLoading || assignmentsLoading;
  const isError = lockersError || assignmentsError;

  return (
    <div className="text-left">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-medium text-gray-700">
          Locker key <span className="text-gray-400 font-normal">(optional)</span>
        </h4>
        <div className="flex items-center gap-2">
          <RoomChip room={room} />
          {/* Count only once loaded — an empty cache would read "0 free". */}
          {!isLoading && !isError && (
            <span className="text-xs text-gray-500">{freeCount} free</span>
          )}
        </div>
      </div>

      {alreadyHeld.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 mb-2 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>
            This member already holds{' '}
            {alreadyHeld
              .map((a) => (a.locker ? `${formatRoom(a.locker.room)} ${a.locker.label}` : 'a key'))
              .join(', ')}
            . You can still give out another key.
          </span>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
        </div>
      ) : isError ? (
        <div className="flex items-start gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-600">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-gray-400" />
          <span>
            Couldn&apos;t load lockers. You can still check the member in — hand out a key
            from the Lockers page afterwards.
          </span>
        </div>
      ) : (
        <>
          <LockerGrid
            lockers={roomLockers}
            room={room}
            openByLockerId={openByLockerId}
            now={now}
            selectedLockerId={selectedLockerId}
            onSelectFree={(locker) => onSelect(locker.id === selectedLockerId ? null : locker.id)}
            gridClassName="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-1.5"
          />
          <p className="mt-2 text-xs text-gray-500">
            Tap a free locker to give out its key with this check-in. Tap again to deselect.
          </p>
        </>
      )}
    </div>
  );
}
